import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLayerStore } from '../stores/layerStore';
import { documentsApi } from '../services/api';
import { queryKeys } from '../queries/queryKeys';

// Module-level dedup to prevent multiple hook instances probing the same doc
const probedDocuments = new Set<string>();
const inFlightProbes = new Map<string, Promise<void>>();

export function clearCommentsLayerProbeCache(documentId?: string) {
  if (documentId) {
    probedDocuments.delete(documentId);
    inFlightProbes.delete(documentId);
  } else {
    probedDocuments.clear();
    inFlightProbes.clear();
  }
}

/**
 * Plan 170 — Auto-detects whether a document has Word comments and registers
 * the 'comments' layer.
 *
 * Fast path: If has_comments boolean is available from the documents list
 * API, the layer is registered instantly without an API call. Leave
 * has_comments UNDEFINED when missing (not coerced to false) so the slow
 * path can probe — coercing to false would call removeAvailableLayer and
 * suppress the layer.
 *
 * Slow path (fallback): Fetches /comments and inspects comments_by_clause.
 */
export function useCommentsLayerDetection(
  documentId: string | null | undefined,
  hasComments?: boolean
) {
  const addAvailableLayer = useLayerStore((s) => s.addAvailableLayer);
  const removeAvailableLayer = useLayerStore((s) => s.removeAvailableLayer);
  const queryClient = useQueryClient();

  // Fast path: use has_comments from document metadata (no API call)
  useEffect(() => {
    if (!documentId || hasComments === undefined) return;

    if (hasComments) {
      addAvailableLayer(documentId, 'comments');
      probedDocuments.add(documentId);
    } else {
      removeAvailableLayer(documentId, 'comments');
      probedDocuments.add(documentId);
    }
  }, [documentId, hasComments, addAvailableLayer, removeAvailableLayer]);

  // Slow path: fetch /comments if metadata not available
  // Also pre-populates the query cache so the card view renders instantly
  useEffect(() => {
    if (!documentId) return;
    if (probedDocuments.has(documentId)) return;
    if (inFlightProbes.has(documentId)) return;
    // If metadata provided, fast path handles it
    if (hasComments !== undefined) return;

    let cancelled = false;

    const probe = documentsApi
      .getComments(documentId)
      .then((res) => {
        if (cancelled) return;
        // apiClient wraps the body in AxiosResponse.data; the envelope has its
        // own `.data.comments_by_clause` so the chain is res.data.data.*
        const body = res?.data;
        const byClause = body?.data?.comments_by_clause ?? {};
        if (Object.keys(byClause).length > 0) {
          // Pre-populate cache with the unwrapped body so the React Query
          // hook in useDocumentComments reads it without a second fetch.
          queryClient.setQueryData(queryKeys.documents.comments(documentId), body);
          addAvailableLayer(documentId, 'comments');
        } else {
          removeAvailableLayer(documentId, 'comments');
        }
        probedDocuments.add(documentId);
      })
      .catch(() => {
        if (!cancelled) {
          probedDocuments.add(documentId);
        }
      });

    inFlightProbes.set(documentId, probe);
    probe.finally(() => inFlightProbes.delete(documentId));

    return () => {
      cancelled = true;
    };
  }, [documentId, hasComments, addAvailableLayer, removeAvailableLayer, queryClient]);
}
